import { getCreatorApiUrl } from '@client/components/api-urls';
import auth from '@client/components/auth';
import axios from 'axios';

type PaginationOptions = {
  page: number;
  pageSize: number;
};

type Paginated<T> = {
  records: T[];
  meta: { totalItems: number; itemsOnPage: number; totalPages: number };
};

type SubscribedResponse = Paginated<unknown>;
export function getSubscribedUsers(
  influencerId: number,
  options: {
    pagination?: PaginationOptions;
    status: string[];
    classification: string[];
    type: string[];
    searchTerm?: string;
    exportCsv?: boolean;
    label: string[];
  },
): Promise<SubscribedResponse> {
  const url = new URL(
    getCreatorApiUrl(
      `/crm/${influencerId}/subscribers${options.exportCsv ? '/export' : ''}`,
    ),
  );

  if (options.pagination) {
    url.searchParams.append(
      'pagination[page]',
      options.pagination.page.toString(),
    );
    url.searchParams.append(
      'pagination[pageSize]',
      options.pagination.pageSize.toString(),
    );
  }

  if (options.status.length >= 1)
    url.searchParams.append('status', options.status.join(','));
  if (options.classification.length >= 1)
    url.searchParams.append('classification', options.classification.join(','));
  if (options.type.length >= 1)
    url.searchParams.append('type', options.type.join(','));
  if (options.label.length >= 1)
    url.searchParams.append('label', options.label.join(','));
  if (options.searchTerm) {
    url.searchParams.append('searchTerm', options.searchTerm);
  }
  if (options.exportCsv) {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const utcOffset = new Date().getTimezoneOffset();

    url.searchParams.append('locale', locale);
    url.searchParams.append('utcOffset', String(utcOffset));
  }

  return axios
    .get<SubscribedResponse>(url.toString(), {
      headers: {
        Authorization: auth.getToken(),
      },
    })
    .then((res) => res.data);
}

type OtpResponse = Paginated<unknown>;
export function getOtpUsers(
  influencerId: number,
  options: {
    pagination?: PaginationOptions;
    status: string[];
    programId: string[];
    searchTerm?: string;
    exportCsv?: boolean;
  },
): Promise<OtpResponse> {
  const url = new URL(
    getCreatorApiUrl(
      `/crm/${influencerId}/one-time-purchases${
        options.exportCsv ? '/export' : ''
      }`,
    ),
  );

  if (options.pagination) {
    url.searchParams.append(
      'pagination[page]',
      options.pagination.page.toString(),
    );
    url.searchParams.append(
      'pagination[pageSize]',
      options.pagination.pageSize.toString(),
    );
  }

  if (options.status.length >= 1)
    url.searchParams.append('status', options.status.join(','));
  if (options.programId.length >= 1) {
    url.searchParams.append('programIds', options.programId.join(','));
  }
  if (options.searchTerm) {
    url.searchParams.append('searchTerm', options.searchTerm);
  }
  if (options.exportCsv) {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const utcOffset = new Date().getTimezoneOffset();

    url.searchParams.append('locale', locale);
    url.searchParams.append('utcOffset', String(utcOffset));
  }

  return axios
    .get<OtpResponse>(url.toString(), {
      headers: {
        Authorization: auth.getToken(),
      },
    })
    .then((res) => res.data);
}

type LeadsResponse = Paginated<unknown>;
export function getLeadsUsers(
  influencerId: number,
  options: {
    pagination?: PaginationOptions;
    source: string[];
    searchTerm?: string;
    exportCsv?: boolean;
  },
): Promise<LeadsResponse> {
  const url = new URL(
    getCreatorApiUrl(
      `/crm/${influencerId}/leads${options.exportCsv ? '/export' : ''}`,
    ),
  );

  if (options.pagination) {
    url.searchParams.append(
      'pagination[page]',
      options.pagination.page.toString(),
    );
    url.searchParams.append(
      'pagination[pageSize]',
      options.pagination.pageSize.toString(),
    );
  }

  if (options.searchTerm) {
    url.searchParams.append('searchTerm', options.searchTerm);
  }

  if (options.source.length >= 1) {
    url.searchParams.append('source', options.source.join(','));
  }
  if (options.exportCsv) {
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const utcOffset = new Date().getTimezoneOffset();

    url.searchParams.append('locale', locale);
    url.searchParams.append('utcOffset', String(utcOffset));
  }

  return axios
    .get<LeadsResponse>(url.toString(), {
      headers: {
        Authorization: auth.getToken(),
      },
    })
    .then((res) => res.data);
}

type LeadsSource = {
  id: number;
  title: string;
  value: string;
  createdAt: string;
};
export function getLeadsSources(): Promise<LeadsSource[]> {
  const url = new URL(getCreatorApiUrl(`/crm/lead-sources`));
  return axios
    .get<LeadsSource[]>(url.toString(), {
      headers: {
        Authorization: auth.getToken(),
      },
    })
    .then((res) => res.data);
}

/**
 * Neither `fetch` nor `axios` support downloading streamed files right away.
 * This workaround does a browser native html based download - it is able to
 * start the download even before the backend transmits the entire file.
 */
export async function downloadExportAll(influencerId: number) {
  const { data } = await axios.get(
    getCreatorApiUrl(`/crm/${influencerId}/export/sign-token`),
    {
      headers: {
        Authorization: auth.getToken(),
      },
    },
  );

  const url = new URL(getCreatorApiUrl(`/crm/${influencerId}/export/stream`));
  const locale = Intl.DateTimeFormat().resolvedOptions().locale;
  const utcOffset = new Date().getTimezoneOffset();
  url.searchParams.append('locale', locale);
  url.searchParams.append('utcOffset', String(utcOffset));
  url.searchParams.append('signedToken', data);

  const link = document.createElement('a');
  link.href = url.toString();
  link.setAttribute(
    'download',
    `${`crm-users-${new Date().toLocaleDateString()}`.toLocaleLowerCase()}.csv`,
  );
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
}
